class Timeline {
	static STANDALONE = 1;
	static INVISIBLE = 3;

	static fromArray(data, manager) {
		let inst = new Timeline(manager);
		inst._id = data.id;
		inst.showTracking = data.showFeelings;
		inst.orderBy = data.orderBy;
		inst.mode = data.viewMode;
		inst.ownerName = data.ownerName;

		return inst;
	}

	constructor(manager) {
		this.manager = manager;
		this.widget = null;
		this._id;
		this._ownerName;
		this._orderBy;
		this._mode;
		this._elem = null;
		this.lastWidth = 0;
		this.millisecondPerPixel = 1;

		this.scale = new TimelineScale(this);

		this._nameElem = UI.span('timeline-name');

		this._feelingChk = UI.chk();

		this.controls = UI.div(
			'flex-0 flex gap-3 pe-3',
			[
				manager.display == Timeline.INVISIBLE ? null :
				UI.iconChk(this._feelingChk, 'thumbs', 22, {
					'data-tip': 'above',
					'aria-label': _("Show feelings")
				}),
				manager.display == Timeline.INVISIBLE ? null :
				this._shrinkBtn = UI.priIconBtn('angle-up', 24, {
					'click': this.shrink.bind(this)
				}, {
					'data-tip': 'above',
					'aria-label': _("Collapse timeline")
				}),
				manager.display == Timeline.INVISIBLE ? null :
				this._expandBtn = UI.priIconBtn('angle-down', 24, {
					'click': this.expand.bind(this)
				}, {
					'data-tip': 'above',
					'aria-label': _('Expand timeline')
				})
			]
		)
		
		if (manager.display != Timeline.INVISIBLE) {
			this.titleBar = UI.listRow(
				[
					UI.div('flex-1', [
						this._nameElem
					]),
					this.controls
				]
			);
		}
		else {
			this.titleBar = null;
		}

		this._trackingElem = UI.div('tracking-strip norm', [
			this._chartElem = UI.htm('canvas')
		]);

		this._rowElem = UI.div('timeline-row', [
			this._dragElem = UI.div('timeline-row-inner norm', [
				this.titleBar,
				this.scale.bar,
				this._stripsElem = UI.div('row timeline-container mode-1', [
					this.scale.background,
					manager.display == Timeline.INVISIBLE ? null : this._trackingElem,
					this._layersElem = UI.div('layer-strip norm')
				], {
					'mouseover': () => {this.indicatorLineOn();},
					'mouseout' : () => {this.indicatorLineOff();}
				}),
			])
		]);
		
		onEv(this.scale.bar, 'wheel', this.handleScrollZoom.bind(this));
		onEv(this._feelingChk, 'change', this.toggleTracking.bind(this));
		// onEv(this._moreBtn, 'click', this.showMore.bind(this));

		if (manager.display != Timeline.INVISIBLE) {
			this._chart = new Chart(this._chartElem, {
				type: 'line',
				data: {
					datasets: [{
						id: 0,
						lineTension: 0.05,
						label: false,
						fill: false,
						steppedLine: false,
						pointRadius: 1,
						borderWidth: 2,
						borderColor: AC.THEMES[userTheme].primary[600],
						data: []
					}]
				},
				options: {
					legend: {
						display: false
					},
					animation: {
						duration : 0
					},
					scales: {
						xAxes : [{display: false, type: 'linear', position: 'bottom'}],
						yAxes : [
							{id: 'simple', display: false, type: 'linear', ticks : {min: -3.2, max : 3.2}}
							]
					},
					tooltips: {
						enabled: false
					},
					maintainAspectRatio: false
				}
			});
		}

		// onEv(this._layersBtn, 'click', () => {
		// 	manageLayers.open(this);
		// });

		this.scale.init();
		this._feelings = {};
		this._feelingData = new FeelingDataSet();
		this._pins = new TimelinePinList();
		this.layerViews = new OrderedList(this._layersElem);
	}

	get id() {return this._id;} 
	set id(v) {this._id = v;}

	get ownerName() { return this._ownerName; }
	set ownerName (value) {
		this._ownerName = value;
		this._nameElem.innerText = value + '\'s Timeline';
	}
	
	get name() { return this._nameElem.innerText; }

	set showTracking(value) {
		if (value == null) value = 1;
		this._feelingChk.checked = value;
		state(this._trackingElem, this._feelingChk.checked ? 'norm' : 'collapsed');
	}

	set mode(value) {
		if (value == null) value = 3;
		this._mode = value;
		this._stripsElem.className = this._stripsElem.className.replace(/(^|\s+)(mode-)(\d)(?=$|\s+)/gm, '$1$2'+value);
	}
	get orderBy() {return this._orderBy;}
	set orderBy(v) {this._orderBy = v;}

	get rowElem() {return this._rowElem;}
	get dragElem() {return this._dragElem;}
	// get topDownIndex() {return this._topDownIndex--;}

	getOrder() {return this._orderBy;}
	getRow() {return this._rowElem;}
	getDrag() {return this._dragElem;}

	loadData (data) {
		if (data.owner) this._nameElem.innerText = _("My Timeline");
		if (data.feelings) this.loadFeelings(data.feelings);
		if (data.feelingData) this.loadFeelingData(data.feelingData);
		if (data.pinData) this.loadPinData(data.pinData);
		if (data.layers && Array.isArray(data.layers)) {
			for (let i = 0; i < data.layers.length; i++) {
				this.addLayerViews(data.layers[i]);
			}
		}
	}

	loadFeelings(data) {
		if (Array.isArray(data)) for (let i = 0; i < data.length; i++) {
			let feeling = TimelineFeeling.fromArray(this, data[i]);
			this._feelings[feeling.id] = feeling;
		};
	}

	loadFeelingData(data) {
		let chartData = this._chart.data.datasets[0].data;
		
		data.forEach((pointData) => {
			let feelingPoint = FeelingDataPoint.fromArray(this, pointData);
			if (feelingPoint.hasIcon) {
				this._feelingData.addFeeling(feelingPoint);
				this._trackingElem.appendChild(feelingPoint.elem);
				feelingPoint.checkVisible(this.viewStartMilliseconds, this.viewEndMilliseconds);
			}
			chartData.push({x: feelingPoint.time / 1000, y: feelingPoint.level});
		});
		this._feelingData.updateInView(this.viewStartMilliseconds, this.viewEndMilliseconds);
		chartData.sort(function(a,b) {return a.x - b.x});
		this._chart.data.datasets[0].data = chartData;
	}

	loadPinData(data) {
		data.forEach((pinData) => {
			let pin = TimelinePin.fromArray(this, pinData);
			this._pins.addPin(pin);
			pin.checkVisible(this.viewStartMilliseconds, this.viewEndMilliseconds);
			this._trackingElem.appendChild(pin.elem);
		});
		this._pins.updateInView(this.viewStartMilliseconds, this.viewEndMilliseconds);
	}

	addLayerViews(layer) {
		let view = this.manager.getLayer(layer.layerId).getView(this,layer.orderBy);
		this.layerViews.addItem(view);
	}

	refreshConflicts (layer, conflicts) {
		if (!(layer instanceof Layer)){
			layer = this.getLayerById(layer);
		}
		layer.clearConflicts();
		for (var i = 0; i < conflicts.length; i++) {
			layer.addConflict(new TimelineConflict(
				layer,
				conflicts[i].event_a,
				conflicts[i].event_b,
				conflicts[i].start_time * 1000,
				conflicts[i].end_time * 1000
			));
		}
		layer.drawConflicts();
	}

	toggleTracking() {
		state(this._trackingElem, this._feelingChk.checked ? 'norm' : 'collapsed');
		this.layerViews.update();
		API.update(
			`timelines/${this.id}`,
			{
				showFeelings: (this._feelingChk.checked ? 1 : 0)
			}
		)
		.then(reply => {
			showError(reply);
		});
	}

	showMore() {
		this._more.toggle(this._moreBtn);
	}

	showConflicts (layer) {
		this.setActiveLayer(layer);
		var l = layer;
		var i,  ii = l.conflicts.length;
		var prev = null, curr = null, conflict = null;
		if (ii) {
			for (i = 0; i < ii; i++) {
				conflict = l.conflicts[i];
				curr = Math.abs(conflict.startTime - this.originTime);
				if (prev !== null && prev < curr) {
					break;
				}
				prev = curr;
				l.conflictIndex = i;
			}
		}
		else {
			l.conflictIndex = null
		}

		if (l.conflictIndex === null) {
			makeToast(_("No conflicts found in the timeline"));
		}
		else {
			this.displayConflicts();
		}
	}
	shrink() {
		if (this._mode == 0) return; 
		this.mode = --this._mode;
		this.layerViews.update();
		// this.layerViews.stacked = this._mode <= 1;
		this.saveMode();
	}
	expand() {
		if (this._mode == 3) return;
		this.mode = ++this._mode;
		this.layerViews.update();
		// this.layerViews.stacked =  this._mode <= 1;
		this.saveMode();
	}
	saveMode () {
		API.update(
			`timelines/${this.id}`,
			{
				mode: this._mode
			}
		)
		.then(reply => {
			showError(reply);
		});
	}
	
	initContainer(elem) {
		this._elem = elem;

		this.indicatorLine = UI.div('indicator-line hide', [
			this.cursorTimeReadout = UI.div('cursor-time')
		]);

		this.groupBar = UI.div('group-bar hide', [
			this.groupHandle = UI.div('group-handle primary-color')
		], {
			'mouseover': this.onHandle.bind(this, MOVE_GROUP),
			'mousedown touchstart': this.handleDown.bind(this, MOVE_GROUP),
			'mouseout': this.onHandle.bind(this, MOVE_GROUP)
		});

		this.leftHandle = UI.div('leftHandle hide', [
			this.leftFine = UI.div(null, null, {
				'mouseover': this.onHandle.bind(this, MOVE_START_FINE),
				'mousedown touchstart': this.handleDown.bind(this, MOVE_START_FINE),
				'mouseout': this.onHandle.bind(this, MOVE_START_FINE)
			})
		], {
			'mouseover': this.onHandle.bind(this, MOVE_START),
			'mousedown touchstart': this.handleDown.bind(this, MOVE_START),
			'mouseout': this.onHandle.bind(this, MOVE_START)
		});

		this.rightHandle = UI.div('rightHandle hide', [
			this.rightFine = UI.div(null, null, {
				'mouseover': this.onHandle.bind(this, MOVE_END_FINE),
				'mousedown touchstart': this.handleDown.bind(this, MOVE_END_FINE),
				'mouseout': this.onHandle.bind(this, MOVE_END_FINE)
			})
		], {
			'mouseover': this.onHandle.bind(this, MOVE_END),
			'mousedown touchstart': this.handleDown.bind(this, MOVE_END),
			'mouseout': this.onHandle.bind(this, MOVE_END)
		});

		elem.appendChild(this.indicatorLine);
		elem.appendChild(this.groupBar);
		elem.appendChild(this.leftHandle);
		elem.appendChild(this.rightHandle);

		this.startTimeDisplay = new TimeDisplay(this.leftFine);
		this.endTimeDisplay = new TimeDisplay(this.rightFine);

		onEv(this._stripsElem, 'mousemove', (ev) => {
			let time = this.cursorTime(ev, SNAP_DISTANCE);
			let dt = new Date(time);
			this.indicatorLine.style.left = this.timeToOffset(time) + 'px';
			this.cursorTimeReadout.style.top = pointInElem(this._elem, point(ev)).y + 20 + 'px';
			this.cursorTimeReadout.innerText = MinsPast(dt.getUTCHours(), dt.getUTCMinutes());
		});

		this.setWidth(elem.offsetWidth);

		if (typeof ResizeObserver !== 'undefined') {
			new ResizeObserver(() => this.resize()).observe(elem);
		}
	}

	cursorTime(ev, snap) {
		let offset = pointInElem(this._elem, point(ev)).x;
		let utc = this.offsetToTime(offset);
		if (snap) return Math.round(utc / snap) * snap;
		return utc;
	}

	indicatorLineOn() {
		if (this.indicatorLine) state(this.indicatorLine);
	}

	indicatorLineOff() {
		if (this.indicatorLine) state(this.indicatorLine, 'hide');
	}

	onHandle() {}

	handleDown(mode, event) {
		this._dragMode = mode;
		stopEv(event);
		let mgr = this.manager;
		switch (mode) {
			case MOVE_START_FINE:
				mgr.activeInstance.event.beginFineMove(MOVE_START, event);
				break;
			case MOVE_START:
				mgr.activeInstance.event.beginMove(MOVE_START, event);
				break;
			case MOVE_END_FINE:
				mgr.activeInstance.event.beginFineMove(MOVE_END, event);
				break;
			case MOVE_END:
				mgr.activeInstance.event.beginMove(MOVE_END, event);
				break;
			default:
				mgr.activeLayer.beginMove(event);
		}
	}

	updateControls() {
		if (!this.leftHandle) return;

		let mgr = this.manager;
		let top = mgr.activeLayer?.activeView?.rowElem.getBoundingClientRect().bottom
			- this._elem.getBoundingClientRect().top;

		if (!mgr.activeInstance) {
			state(this.leftHandle, 'hide');
			state(this.rightHandle, 'hide');
		}
		else {
			state(this.leftHandle);
			state(this.rightHandle);

			let start = mgr.activeInstance.startTime;
			let end = mgr.activeInstance.endTime;

			this.leftHandle.style.left = this.timeToOffset(start) + 'px';
			this.rightHandle.style.left = this.timeToOffset(end) + 'px';
			this.leftHandle.style.top = top - 30 + 'px';
			this.rightHandle.style.top = top - 30 + 'px';
			this.startTimeDisplay.setTime(start);
			this.endTimeDisplay.setTime(end);
		}

		if (mgr.selection.isEmpty) {
			state(this.groupBar, 'hide');
			return;
		}

		let left = this.timeToOffset(mgr.selection.first.startTime);
		let right = this.timeToOffset(mgr.selection.last.endTime);

		this.groupBar.style.top = top + 'px';
		this.groupHandle.style.left = left + 'px';
		this.groupHandle.style.width = right - left + 'px';

		state(this.groupBar);
	}

	resize() {
		if (this._resizing || !this._elem) return;
		if (this.lastWidth === this._elem.offsetWidth) return;
		this._resizing = true;
		setTimeout(() => {
			this.setWidth(this._elem.offsetWidth);
			this.scaleChange();
			this.update();
			this._resizing = false;
		}, 500);
	}

	mousedown(ev) {
		this._startDatumTime = this.manager.originTime;
		this._dragMode = 0;
		startDrag(ev, this);
	}

	mouseover(ev) {
		if (dragging()) return;
		stopEv(ev);
	}

	mouseup() {}

	mouseout() {}

	drag() {
		let mgr = this.manager;
		switch (this._dragMode) {
			case MOVE_START_FINE:
				mgr.activeLayer.moveEvents(this.startTimeDisplay.scrollTime(dx));
				break;
			case MOVE_END_FINE:
				mgr.activeLayer.moveEvents(this.endTimeDisplay.scrollTime(dx));
				break;
			default:
				let timeDelta = this.pixelsToMilliseconds(dx * 3);
				mgr.originTime = this._startDatumTime - timeDelta;
				mgr.update();
		}
	}

	dropped() {
		if (this.startTimeDisplay) this.startTimeDisplay.settle();
		if (this.endTimeDisplay) this.endTimeDisplay.settle();
		switch (this._dragMode) {
			case MOVE_START_FINE: case MOVE_END_FINE:
				this.manager.activeLayer.dropped();
				break;
			default:
				document.cookie = 'tl-time=' + this.manager.originTime;
				this.manager.getTimelineData();
		}
	}

	clicked() {
		if (this._dragMode === 0 && this.scale.gotoDate !== null) {
			this.manager.goToDate(this.scale.gotoDate);
			this.scale.gotoDate = null;
		}
		if (this._dragMode == 5) {
			this.manager.selection.forEach(inst => inst.event.clicked());
		}
	}

	handleScrollZoom(ev) {
		stopEv(ev);
		ev.preventDefault();
		let ref = this._elem || ev.currentTarget;
		let x = pointInElem(ref, point(ev)).x;
		let cursorTime = this.offsetToTime(x);
		let fraction = x / this.lastWidth;
		this.manager.scrollZoom(cursorTime, fraction, ev.wheelDelta);
	}

	scaleChange() {
		this.setWidth(this.lastWidth);
		this.scale.scaleChange();
	}

	getDaysInView() {
		return this.manager.getDaysInView();
	}

	get viewStartMilliseconds() {
		return this.manager.viewStartMilliseconds;
	}

	get viewEndMilliseconds() {
		return this.manager.viewEndMilliseconds;
	}

	setWidth(width) {
		if (!width) return;
		this.lastWidth = width;
		this.millisecondPerPixel = (MILLISECONDS_PER_DAY * this.getDaysInView()) / width;
	}

	timeToOffset(utc) {
		return Math.floor((utc - this.viewStartMilliseconds) / this.millisecondPerPixel);
	}

	offsetToTime(offset) {
		return this.viewStartMilliseconds + (offset * this.millisecondPerPixel);
	}

	millisecsToPixels(millis) {
		return Math.floor(millis / this.millisecondPerPixel);
	}

	pixelsToMilliseconds(px, snap) {
		if (snap) return Math.round((px * this.millisecondPerPixel) / snap) * snap;
		return Math.round(px * this.millisecondPerPixel);
	}

	update() {
		this.scale.update();
		this._feelingData.updateInView(this.viewStartMilliseconds, this.viewEndMilliseconds);

		if (this._feelingData.moveToFirstInView()) do {
			this._feelingData.item.update();
		} while (this._feelingData.moveToNextInView());

		this._pins.updateInView(this.viewStartMilliseconds, this.viewEndMilliseconds);

		if (this._pins.moveToFirstInView()) do {
			this._pins.item.update();
		} while (this._pins.moveToNextInView());

        if (this._chart){
			this._chart.options.scales.xAxes[0].ticks.min = this.viewStartMilliseconds / 1000;
			this._chart.options.scales.xAxes[0].ticks.max = this.viewEndMilliseconds / 1000;
			this._chart.update();
		}
	}
}